import type { ReactNode } from 'react';
import { CircleAlert, Inbox, RefreshCw, WifiOff } from 'lucide-react';
import { cn } from '@/lib/cn';
import { errorMessage, isNetworkError } from '@/lib/graphql';
import { Button } from './button';

/**
 * Estado vacío.
 *
 * Un estado vacío no es un hueco: es la primera vez que alguien ve esa
 * pantalla. Tiene que decir **qué va aquí** y ofrecer el gesto que lo llena,
 * así que `description` y `action` no son adornos opcionales — un «No hay
 * datos» con un icono deja a la persona exactamente donde estaba.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  hint,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  /** Nota al pie: un atajo, una explicación de por qué está vacío. */
  hint?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-10 text-center',
        className,
      )}
    >
      <div className="flex size-10 items-center justify-center rounded-lg border border-border bg-surface-sunken text-fg-subtle [&_svg]:size-5">
        {icon ?? <Inbox aria-hidden="true" />}
      </div>
      <div className="max-w-[46ch]">
        <p className="text-sm font-semibold text-fg">{title}</p>
        {description ? <p className="mt-1 text-sm text-fg-muted">{description}</p> : null}
      </div>
      {action ? <div className="flex flex-wrap items-center justify-center gap-2">{action}</div> : null}
      {hint ? <p className="text-xs text-fg-subtle">{hint}</p> : null}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  title,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
  className?: string;
}) {
  const network = isNetworkError(error);
  return (
    <div
      // `role="alert"` para que el fallo se anuncie: quien no ve la pantalla
      // no debe quedarse esperando datos que nunca van a llegar.
      role="alert"
      className={cn('flex flex-col items-center justify-center gap-3 px-6 py-12 text-center', className)}
    >
      <div
        className={cn(
          'flex size-12 items-center justify-center rounded-full [&_svg]:size-6',
          network ? 'bg-warning-subtle text-warning-subtle-fg' : 'bg-danger-subtle text-danger-subtle-fg',
        )}
      >
        {network ? <WifiOff aria-hidden="true" /> : <CircleAlert aria-hidden="true" />}
      </div>
      <div className="max-w-prose">
        <p className="text-base font-semibold text-fg">
          {title ?? (network ? 'Sin conexión con el servidor' : 'No se pudieron cargar los datos')}
        </p>
        <p className="mt-1 text-sm text-fg-muted">{errorMessage(error)}</p>
      </div>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          <RefreshCw aria-hidden="true" />
          Reintentar
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Aviso en línea, para errores que no vacían la pantalla (una acción que falló
 * mientras el resto sigue en pie).
 */
export function InlineError({ error, className }: { error: unknown; className?: string }) {
  if (!error) return null;
  return (
    <p
      role="alert"
      className={cn(
        'flex items-start gap-2 rounded-md bg-danger-subtle px-3 py-2 text-sm text-danger-subtle-fg',
        className,
      )}
    >
      <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>{errorMessage(error)}</span>
    </p>
  );
}
